import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { cn } from "../lib/utils";
import {
  LayoutDashboard,
  Ticket,
  Users,
  BookOpen,
  FolderOpen,
  MessageSquare,
  Star,
  Send,
  Puzzle,
  AlertCircle,
  ArrowLeft,
  LogOut,
  MessageSquareText,
  ShieldCheck,
  History,
  Tags,
  Tag,
  ThumbsUp,
  BarChart3,
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "./ui/sidebar";
import { getAdminMe } from "../pages/support-system/supportApi";
import SupportNotificationBell from "./SupportNotificationBell";
import AppSwitcher from "./AppSwitcher";
import { useDispatch, useSelector } from "react-redux";
import { logoutUser } from "../slices/authSlice";

const adminNavGroups = [
  {
    label: "Overview",
    items: [
      { name: "Dashboard", path: "/support-system/dashboard", icon: LayoutDashboard },
      { name: "Analytics", path: "/support-system/analytics", icon: BarChart3 },
      { name: "Activity Log", path: "/support-system/activity-log", icon: History },
    ],
  },
  {
    label: "Tickets",
    items: [
      { name: "Support Tickets", path: "/support-system/tickets", icon: Ticket },
      { name: "Workflow Tags", path: "/support-system/workflow-tags", icon: Tag },
      { name: "Agent Dashboard", path: "/support-system/agents", icon: Users },
    ],
  },
  {
    label: "Content",
    items: [
      { name: "Articles / KB", path: "/support-system/articles-kb", icon: BookOpen },
      // Manages product sections (gidex, ginox_ai …), not article categories — those
      // live under Article Categories & Tags. The route keeps its old name.
      { name: "Products", path: "/support-system/categories", icon: FolderOpen },
      { name: "Article Categories & Tags", path: "/support-system/article-taxonomy", icon: Tags },
      { name: "Article Feedback", path: "/support-system/article-feedback", icon: ThumbsUp },
      { name: "Saved Replies", path: "/support-system/saved-replies", icon: MessageSquareText },
    ],
  },
  {
    label: "Feedback",
    items: [
      { name: "Feedback", path: "/support-system/feedback", icon: MessageSquare },
      { name: "LiveChat Feedback", path: "/support-system/live-chat-feedback", icon: Star },
    ],
  },
  {
    label: "Setup",
    items: [
      { name: "Telegram Setup", path: "/support-system/telegram-setup", icon: Send },
      { name: "Widget Integration", path: "/support-system/widget-integration", icon: Puzzle },
      { name: "Admin Access", path: "/support-system/admin-access", icon: ShieldCheck },
    ],
  },
];

function NavItem({ item }) {
  const location = useLocation();
  const isActive = location.pathname === item.path;
  const Icon = item.icon;

  return (
    <Link
      to={item.path}
      className={cn(
        "relative flex items-center gap-3 rounded-md py-1.5 pl-3 pr-3 text-sm transition-colors",
        isActive
          ? "bg-[#1a1d23] text-foreground before:absolute before:left-0 before:top-1/2 before:h-5 before:w-px before:-translate-y-1/2 before:bg-ginox-green"
          : "text-muted-foreground hover:bg-white/[0.04] hover:text-foreground"
      )}
    >
      <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
      {item.name}
    </Link>
  );
}

// This panel is admin-only. Agents have their own workspace in the ginox-frontend app and
// never sign in here.
function SupportSidebar() {
  const navGroups = adminNavGroups;

  return (
    <Sidebar
      collapsible="offcanvas"
      className="border-r border-sidebar-border"
      style={{ "--sidebar-background": "10 12 16" }}
    >
      <SidebarHeader className="h-16 flex-row items-center gap-3 px-5 border-b border-sidebar-border bg-[#0a0c10]">
        <img
          src={process.env.PUBLIC_URL + "/logo.png"}
          alt="Ginox"
          className="h-8 w-8 shrink-0 object-contain"
        />
        <div>
          <span className="font-serif text-[20px] italic leading-none text-sidebar-foreground">
            Support
          </span>
          <p className="text-[9px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/50 select-none mt-0.5">
            Admin Panel
          </p>
        </div>
      </SidebarHeader>

      <SidebarContent className="bg-[#0a0c10] px-2 pt-3 pb-4 overflow-y-auto">
        {navGroups.map((group) => (
          <SidebarGroup key={group.label} className="py-1">
            <SidebarGroupLabel className="px-3 text-[9px] font-semibold uppercase tracking-[0.2em] text-muted-foreground/60">
              {group.label}
            </SidebarGroupLabel>
            <SidebarGroupContent className="space-y-0.5">
              {group.items.map((item) => (
                <NavItem key={item.path} item={item} />
              ))}
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </SidebarContent>
    </Sidebar>
  );
}

const SupportLayout = ({ children }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // The shared admin session. Support no longer keeps a token of its own — arriving here
  // from another panel's app switcher is simply a signed-in visit, with no second login.
  const { access_token: token, user: authUser } = useSelector((s) => s.auth);
  const [user, setUser] = useState(null);
  const [checked, setChecked] = useState(false);
  // Only ever set true by an explicit 403. A check that fails to run leaves it false, so
  // a network problem keeps the panel open rather than locking the admin out.
  const [accessRevoked, setAccessRevoked] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate("/support-system/login");
      setChecked(true);
      return;
    }

    // Show the cached identity straight away so a switch into this panel does not blank —
    // the /me call below refreshes it. `supportUser` stays in localStorage because the
    // support pages read it for the `role` this panel stamps on itself.
    const cached = localStorage.getItem("supportUser");
    if (cached) setUser(JSON.parse(cached));

    // This is the admin gate. Being signed in to Ginox does not make someone a support
    // admin: the backend reads its Admin table on every request, so both "never had
    // access" and "revoked mid-session" surface here as a 403.
    // A 401 is handled inside supportApiFetch, which ends the shared session.
    getAdminMe()
      .then(async (res) => {
        if (res.status === 403) {
          setAccessRevoked(true);
          return;
        }
        if (!res.ok) return;
        const me = await res.json();
        // role is not part of /admin/me — the panel's own pages read it, so stamp it here.
        const next = { ...me, role: "admin" };
        localStorage.setItem("supportUser", JSON.stringify(next));
        setUser(next);
      })
      .catch(() => {})
      .finally(() => {
        // ponytail: a support backend that is merely unreachable — a network error, a 502
        // — must not read as "you were demoted". Only a 403 locks the panel; every other
        // failure falls back to the shared session's identity so the panel still renders
        // and individual pages surface their own errors.
        setUser((u) => u || authUser);
        setChecked(true);
      });
  }, [navigate, token, authUser]);

  const handleLogout = () => {
    localStorage.removeItem("supportUser");
    dispatch(logoutUser());
    navigate("/support-system/login");
  };

  if (!checked) return null;
  // accessRevoked is checked before `user`, because a 403 on a fresh visit leaves no
  // identity to show and the card below is exactly what that person needs to read.
  if (!accessRevoked && !user) return null;

  if (accessRevoked) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-4">
        <div className="w-full max-w-sm rounded-xl border border-border/70 bg-card p-6 text-center shadow-xl">
          <AlertCircle
            className="mx-auto h-8 w-8 text-red-400"
            aria-hidden="true"
          />
          <h1 className="mt-4 text-sm font-medium text-foreground">
            No support admin access
          </h1>
          <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
            You are signed in, but this account is not a support admin. Signing
            in again will not change that — ask an administrator for access.
          </p>
          {/* Leaving is the usual answer, not signing out: the session is shared with the
              other panels now, so a wrong turn into Support must not cost it. */}
          <div className="mt-5 flex items-center justify-center gap-2">
            <button
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-2 rounded-md border border-border/70 bg-white/[0.02] px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-white/[0.05] hover:text-foreground"
            >
              <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
              Back to Ginox
            </button>
            <button
              onClick={handleLogout}
              className="inline-flex items-center gap-2 rounded-md border border-border/70 bg-white/[0.02] px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-white/[0.05] hover:text-foreground"
            >
              <LogOut className="h-3.5 w-3.5" aria-hidden="true" />
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <SidebarProvider defaultOpen={true}>
      <SupportSidebar />
      <SidebarInset className="bg-background min-h-screen flex flex-col min-w-0 overflow-hidden">
        {/* Topbar */}
        <header
          className="flex h-14 shrink-0 items-center gap-3 border-b border-border px-4 sm:h-16 sm:px-5"
          style={{ backgroundColor: "#0b0e11" }}
        >
          <SidebarTrigger className="h-9 w-9 rounded-md border border-border/70 bg-white/[0.02] text-muted-foreground transition-colors hover:bg-white/[0.05] hover:text-foreground [&_svg]:h-4 [&_svg]:w-4" />

          <div className="flex min-w-0 flex-1 items-center gap-2 font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
            <span className="hidden sm:inline text-muted-foreground/50">Ginox</span>
            <span className="hidden sm:inline text-muted-foreground/30">/</span>
            <span className="text-foreground">Support Panel</span>
          </div>

          <div className="flex items-center gap-3">
            <AppSwitcher inApp />
            <SupportNotificationBell />
            {user && (
              <span className="hidden sm:block truncate max-w-[160px] text-xs text-muted-foreground">
                {user.name || user.email}
              </span>
            )}
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 rounded-md border border-border/70 bg-white/[0.02] px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-white/[0.05] hover:text-foreground"
            >
              <LogOut className="h-3.5 w-3.5" aria-hidden="true" />
              <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </header>

        <main className="flex-1">
          <div className="mx-auto max-w-[1440px] px-5 py-5 sm:px-8 sm:py-6 md:px-10 md:py-7">
            {children}
          </div>
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
};

export default SupportLayout;
